/**
 * Squelette du planning (docs/ux-v2/SPEC.md § 12) : passage de relais entre le squelette rendu par le serveur dans
 * #mindbody-app (apps/server/UxV2Skeleton.php, visible avant tout JS) et celui de React (screens/Planning/Skeleton.tsx),
 * sans rupture :
 * - même reflet, à la même phase : mountV2 relève, avant le premier rendu (qui efface le balisage du serveur),
 *   l'instant de départ de l'animation du serveur ; chaque liste de cartes en attente de React cale ses animations sur
 *   cet instant (sinon sur l'origine de la page), au lieu de repartir de zéro ;
 * - un seul message « Chargement du planning… » : celui du serveur ; pendant ce premier chargement, le message d'état
 *   de React reste vide (la zone d'état de React, insérée au montage, ne le répète pas) ;
 * - apparition des cartes en fondu léger quand elles remplacent un squelette (opacité seulement, rien ne bouge) ;
 * - même police : pour une police connue, le script en ligne du serveur (apps/server/UxV2Skeleton.js) a déjà déclaré
 *   l'alias aux métriques corrigées de theme/fontAlias.ts et en a peint les libellés du squelette ; mountV2 le reprend
 *   dès son premier rendu (serverFontAliases), au lieu de repartir de la police d'origine puis de corriger.
 */
import { aliasFamilyName, parseFontStack, stackWithAlias } from '../fontMetrics';


/** Nom de l'animation du reflet (apps/server/UxV2Skeleton.css). */
export const SWEEP_ANIMATION = 'mmv2-sk-sweep';

/** Attribut de la racine du squelette du serveur (UxV2Skeleton::MARKER). */
export const SERVER_SKELETON_MARKER = 'data-mmv2-skeleton';

/** Familles dont le serveur a déclaré l'alias (UxV2Skeleton.js : data-font-alias, data-font-display-alias). */
export interface ServerFontAliases {
  font: string | null;
  fontDisplay: string | null;
}

interface ServerSkeletonState {
  /** Le squelette du planning du serveur était affiché au montage (pas une autre route, pas la variante neutre). */
  shown: boolean;
  /** Instant de départ (horloge du document, ms) du reflet du serveur ; null s'il n'y en a pas. */
  origin: number | null;
  fonts: ServerFontAliases;
}

const NO_FONTS: ServerFontAliases = { font: null, fontDisplay: null };

let state: ServerSkeletonState = { shown: false, origin: null, fonts: NO_FONTS };

type AnimationLike = { startTime: number | string | null | { value?: number } } & Partial<{ animationName: string }>;

/** Animations du reflet d'un élément et de ses descendants (pseudo-éléments compris), [] si l'API manque. */
function sweepAnimations(element: Element): AnimationLike[] {
  if (typeof element.getAnimations !== 'function') return [];
  try {
    return (element.getAnimations({ subtree: true }) as unknown as AnimationLike[]).filter(
      (animation) => animation.animationName === SWEEP_ANIMATION,
    );
  } catch {
    return [];
  }
}

const toMs = (time: AnimationLike['startTime']): number | null => {
  if (typeof time === 'number' && Number.isFinite(time)) return time;
  if (time && typeof time === 'object' && typeof time.value === 'number') return time.value;
  return null;
};

const familyAttribute = (root: HTMLElement, name: string): string | null => {
  const value = root.getAttribute?.(name)?.trim();
  return value ? value : null;
};

/**
 * Relevé du squelette du serveur, avant le premier rendu de React (mountV2). Masqué (`hidden` : retour d'un paiement)
 * ou absent (mode coach, flag allumé par aperçu sans balisage) : rien. Variante neutre (`data-route="ecran"` : autre
 * écran que le planning) : pas de squelette du planning à reprendre ; l'alias de police, si.
 */
export function captureServerSkeleton(container: Element): void {
  const root = container.querySelector<HTMLElement>(`[${SERVER_SKELETON_MARKER}]`);
  if (!root) {
    state = { shown: false, origin: null, fonts: NO_FONTS };
    return;
  }
  const fonts = {
    font: familyAttribute(root, 'data-font-alias'),
    fontDisplay: familyAttribute(root, 'data-font-display-alias'),
  };
  if (root.hidden || root.getAttribute?.('data-route') === 'ecran') {
    state = { shown: false, origin: null, fonts };
    return;
  }
  const first = sweepAnimations(root)
    .map((animation) => toMs(animation.startTime))
    .find((time) => time !== null);
  state = { shown: true, origin: first ?? null, fonts };
}

/**
 * Pile de la v2 avec l'alias déjà déclaré par le serveur pour sa famille (même place que theme/fontAlias.ts
 * alignFontStack : juste devant la famille corrigée) ; la pile telle quelle si le serveur n'a rien déclaré ou si la
 * famille n'est pas dans cette pile.
 */
export function withServerFontAlias(stack: string, family: string | null): string {
  if (!family) return stack;
  const has = parseFontStack(stack).some((name) => name.toLowerCase() === family.toLowerCase());
  return has ? stackWithAlias(aliasFamilyName(family), stack, family) : stack;
}

/** Familles dont le serveur a déclaré l'alias (relevé de captureServerSkeleton). */
export function serverFontAliases(): ServerFontAliases {
  return state.fonts;
}

/** Le squelette du serveur était affiché et le premier chargement du planning n'est pas fini. */
export function serverSkeletonPending(): boolean {
  return state.shown;
}

/** Premier chargement fini (cartes, semaine vide ou erreur) : les chargements suivants ont leur propre message. */
export function releaseServerSkeleton(): void {
  state = { ...state, shown: false };
}

/**
 * Cale les reflets d'une liste de cartes en attente sur ceux du serveur (ou sur l'origine de la page) : même phase
 * partout, le reflet continue son passage au lieu de repartir à gauche quand React remplace le balisage du serveur.
 */
export function alignSkeletonAnimations(element: Element | null): void {
  if (!element) return;
  const origin = state.origin ?? 0;
  sweepAnimations(element).forEach((animation) => {
    try {
      animation.startTime = origin;
    } catch {
      // Navigateur qui refuse : l'animation garde sa phase.
    }
  });
}

/** Une carte en attente par colonne porte une playlist (127 px au lieu de 109), en quinconce (= UxV2Skeleton::isTallCard). */
export const isTallSkeletonCard = (day: number, card: number) => (3 * day + card) % 4 === 1;

/** Durée du fondu d'apparition des cartes (ms). */
export const REVEAL_DURATION = 280;

/**
 * Opacité de départ du fondu : pas 0. React remplace les cartes en attente par les cartes dans le même rendu ; en
 * partant de 0, la première image qui suivait n'avait plus ni l'un ni l'autre (zone du planning vide, films du 09/10).
 * À 0,35, les surfaces des cartes sont déjà là et leur texte se précise.
 */
export const REVEAL_FROM = 0.35;

/** Mouvement réduit demandé par le visiteur : ni reflet (UxV2Skeleton.css) ni fondu, les cartes s'affichent d'un coup. */
const prefersReducedMotion = () => {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/** Fondu d'apparition (opacité seulement : aucune boîte ne bouge) des éléments qui remplacent un squelette. */
export function revealElements(elements: Iterable<Element>): void {
  if (prefersReducedMotion()) return;
  for (const element of elements) {
    if (typeof (element as HTMLElement).animate !== 'function') continue;
    try {
      (element as HTMLElement).animate([{ opacity: REVEAL_FROM }, { opacity: 1 }], {
        duration: REVEAL_DURATION,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      });
    } catch {
      // Sans Web Animations : apparition immédiate.
    }
  }
}

/** Réinitialisation (tests). */
export function resetServerSkeletonForTests(next: Partial<ServerSkeletonState> = {}): void {
  state = { shown: false, origin: null, fonts: NO_FONTS, ...next };
}
